import { trackConfig, type IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import type { Layer } from '@/layers/layer';
import { checkIsDarkColor } from '@/utils/color-kit';
import { getTextLayerProps } from '@/utils/layer-kit';
import {
    LayerType,
    MaterialIdsType,
    TextAlignType,
    TextWritingType,
    type IBackgroundLayerProps,
    type ILayerProps,
    type IPosition,
    type ISize,
    type ITextLayerProps,
} from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';
import { getDefaultLayerWithEffectsForShortcut, getDefaultShapeTextLayerForShortcut } from '../config';
import {
    DEFAULT_FONT_COLOR,
    DEFAULT_LINE_HEIGHT_FACTOR,
    DEFAULT_TEXT_GRADIENT,
    getDefaultTextByLocale,
    USE_ENGINE_KEY,
} from '../constants';
import { estimateLayerSize, measureLayer } from '../utils';

export interface IShortcut {
    /**
     * 创建默认文本图层
     * @param partial - 部分文本图层数据，可以通过传入字号、字体、颜色等属性来创建默认文本图层
     * @param position - 位置，可选，如果传入则会在创建时自动居中，如果传入 left 和 top 则会在创建时自动设置位置
     * @param stageSize - 目标画布尺寸，新建画布尚未插入时用于计算默认字号和居中位置
     * @returns 未插入画布的文本图层
     */
    createDefaultTextLayer(
        partial: Partial<ITextLayerProps>,
        position?: Partial<IPosition>,
        stageSize?: ISize,
    ): ILayerProps;

    /**
     * 文本图层快捷方式：添加默认标题
     * @param options - 配置项
     *  - byMousePosition - 是否通过鼠标位置添加
     * @returns
     */
    addDefaultTitle(options?: { byMousePosition?: boolean }): ILayerProps;

    /**
     * 文本图层快捷方式：添加默认子标题
     * @param options - 配置项
     *  - byMousePosition - 是否通过鼠标位置添加
     * @returns
     */
    addDefaultSubTitle(options?: { byMousePosition?: boolean }): ILayerProps;

    /**
     * 文本图层快捷方式：添加默认正文
     * @param options - 配置项
     *  - byMousePosition - 是否通过鼠标位置添加
     * @returns
     */
    addDefaultContent(options?: { byMousePosition?: boolean }): ILayerProps;

    /**
     * 文本图层快捷方式：添加默认变形文字
     * @param materialID - 素材 ID，由于各环境的素材 id 存在差异，需要传由外部传入
     * @returns
     */
    addDefaultShapeText(materialID: string): ILayerProps;

    /**
     * 文本图层快捷方式：添加默认特效文字
     * @returns
     */
    addDefaultEffectText(): ILayerProps;

    /**
     * 文本图层快捷方式：通过自定义文本图层数据创建文本图层
     * @param partial - 部分文本图层数据
     * @param options - 配置项
     *  - byMousePosition - 是否通过鼠标位置添加
     * @returns
     */
    addWithCustomLayerData(
        partial: Partial<ITextLayerProps>,
        options?: { byMousePosition?: boolean; maxWidth?: number },
    ): Promise<ILayerProps>;
}

const DEFAULT_LIGHT_COLOR = '#ffffffff';
const DEFAULT_DARK_COLOR = '#000000ff';

export class Shortcut implements IShortcut {
    constructor(
        private framework: IFramework,
        private baseLayer: Layer,
    ) {}

    createDefaultTextLayer(partial: Partial<ITextLayerProps>, position?: Partial<IPosition>, stageSize?: ISize) {
        const defaultFontSize = this.resolvePresetFontSize('subTitle', stageSize);
        const defaultText = getDefaultTextByLocale(getLocaleStore().locale);
        const defaultFontFamily = DEFAULT_FONT_FAMILY().name;

        const data: ITextLayerProps = {
            text: defaultText,
            bold: false,
            color: DEFAULT_FONT_COLOR,
            fontFamily: defaultFontFamily,
            fontSize: defaultFontSize,
            italic: false,
            letterSpacing: 0,
            lineSpacing: DEFAULT_LINE_HEIGHT_FACTOR,
            lineThrough: false,
            underline: false,
            textAlign: TextAlignType.CENTER,
            writingMode: TextWritingType.HORIZONTAL_TB,
            textGlow: [],
            textShadow: [],
            textStroke: [],
            textBackground: [],
            listStyle: '',
            textGradient: DEFAULT_TEXT_GRADIENT,
            textContents: [],
            ...partial,
        };

        const layer = getTextLayerProps({
            flipH: false,
            flipV: false,
            materialIds: {},
            data,
            extraInfo: { modified: false, [USE_ENGINE_KEY]: true },
            __runtime: { isInit: true }, // ATTENTION: 本地创建的图层 __runtime.isInit 为 true
            __fixedWidth: false, // 新建默认文本按内容自适应宽度
        });

        // 按实际使用的字体记录素材，避免自定义字体被标成默认字体
        const font = this.framework.service.font.getByFamily(data.fontFamily);
        if (font) layer.materialIds = { [MaterialIdsType.FONT_FAMILY]: [font.id] };

        // 测量图层尺寸
        const engine = this.framework.service.textEngine.getEngine();
        if (!engine || engine.isDisposed) {
            // 正常情况下不会走到这里，主要是兜底处理
            const estimated = estimateLayerSize(layer);
            layer.width = estimated.width;
            layer.height = estimated.height;
        } else {
            const result = measureLayer(engine, layer);
            layer.width = result.width;
            layer.height = result.height;
        }

        const isEdgelessMode = this.framework.service.edgelessCanvas.isEdgelessMode;
        const targetSize = stageSize ?? (isEdgelessMode ? null : this.framework.service.frame.getActiveFrameSize());

        if (targetSize) {
            layer.left = (targetSize.width - layer.width) / 2;
            layer.top = (targetSize.height - layer.height) / 2;
        } else {
            // TODO: 无限画布场景下 position 确认
            layer.left = 0;
            layer.top = 0;
        }

        if (position) {
            if (position.left !== undefined) layer.left = position.left;
            if (position.top !== undefined) layer.top = position.top;
        }

        return layer;
    }

    private resolvePresetFontSize(kind: 'title' | 'subTitle' | 'content', targetSize?: ISize) {
        const isEdgelessMode = this.framework.service.edgelessCanvas.isEdgelessMode;
        const stageSize = targetSize ?? (isEdgelessMode ? null : this.framework.service.frame.getActiveFrameSize());

        if (!stageSize) return 120;

        switch (kind) {
            case 'title':
                return Number(Math.round(stageSize.width / 21).toFixed(0)) * 2;
            case 'subTitle':
                return Number(Math.round(stageSize.width / 38).toFixed(0)) * 2;
            case 'content':
                return Number(Math.round(stageSize.width / 55).toFixed(0)) * 2;
        }
    }

    private checkIsDarkBackground() {
        const stageLayers = this.framework.service.frame.getActiveFrameLayers();
        if (stageLayers.length === 0) return false;

        const backgroundLayer = stageLayers.find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);
        if (!backgroundLayer) return false;

        const layerData = backgroundLayer.data as IBackgroundLayerProps;
        return checkIsDarkColor(layerData.backgroundColor);
    }

    private resolveDefaultTextColor() {
        const isDark = this.checkIsDarkBackground();
        return isDark ? DEFAULT_LIGHT_COLOR : DEFAULT_DARK_COLOR;
    }

    private add(layer: ILayerProps, options: { byMousePosition?: boolean } = {}) {
        const FUNC_ID = trackConfig[LayerType.TEXT_LAYER].add;

        if (options.byMousePosition) {
            return this.baseLayer.addByMousePosition({ layer, trackFunc: FUNC_ID });
        }
        return this.baseLayer.addByStraight({ layer, trackFunc: FUNC_ID });
    }

    addDefaultTitle(options = { byMousePosition: false }) {
        const fontSize = this.resolvePresetFontSize('title');
        const color = this.resolveDefaultTextColor();

        const layer = this.createDefaultTextLayer({ fontSize, color });
        return this.add(layer, options);
    }

    addDefaultSubTitle(options = { byMousePosition: false }) {
        const fontSize = this.resolvePresetFontSize('subTitle');
        const color = this.resolveDefaultTextColor();

        const layer = this.createDefaultTextLayer({ fontSize, color });
        return this.add(layer, options);
    }

    addDefaultContent(options = { byMousePosition: false }) {
        const fontSize = this.resolvePresetFontSize('content');
        const color = this.resolveDefaultTextColor();

        const layer = this.createDefaultTextLayer({ fontSize, color });
        return this.add(layer, options);
    }

    addDefaultShapeText(materialID: string) {
        const stageSize = this.framework.service.frame.getActiveFrameSize();

        const isDarkBG = this.checkIsDarkBackground();
        const layer = getDefaultShapeTextLayerForShortcut(stageSize, materialID, isDarkBG);

        return this.add(layer);
    }

    addDefaultEffectText() {
        const stageSize = this.framework.service.frame.getActiveFrameSize();
        const layer = getDefaultLayerWithEffectsForShortcut(stageSize);
        return this.add(layer);
    }

    /**
     * 加载回退字体后创建文字图层，超过指定宽度时改为固定宽度换行并保持中心位置
     *
     * @param partial - 文字内容和样式
     * @param options - 插入位置选项及文字框宽度上限
     * @returns 插入后的文字图层
     */
    async addWithCustomLayerData(
        partial: Partial<ITextLayerProps>,
        options: { byMousePosition?: boolean; maxWidth?: number } = {},
    ) {
        // bugfix: 确保 fallback 字体已加载
        // 场景复现：
        // 在海外环境启动应用（即默认回退字体是英文，字体不包含中文字符），通过粘贴纯文本新建一个文本图层
        // 可以正常渲染，但是图层尺寸不对，原因是 createDefaultTextLayer 直接使用图层字体进行测量，
        // 但是海外的默认字体 Poppins 不包含中文字符，导致测量结果不准确，这里需要确保 fallback 字体已加载
        await this.framework.service.font.ensureFallbacks(partial.text ?? '');

        const layer = this.createDefaultTextLayer(partial);
        const { maxWidth } = options;
        if (maxWidth !== undefined && Number.isFinite(maxWidth) && maxWidth > 0 && layer.width > maxWidth) {
            const originalWidth = layer.width;
            const originalHeight = layer.height;
            layer.width = maxWidth;
            layer.__fixedWidth = true;

            const engine = this.framework.service.textEngine.getEngine();
            layer.height =
                engine && !engine.isDisposed
                    ? measureLayer(engine, layer).height
                    : originalHeight * Math.ceil(originalWidth / maxWidth);
            layer.left += (originalWidth - layer.width) / 2;
            layer.top += (originalHeight - layer.height) / 2;
        }
        return this.add(layer, options);
    }
}
